// Workflow trigger helpers: catalog normalisation, field visibility, payload building and the
// small formatting bits the triggers UI needs. Kept pure so they are easy to reason about.

export const TRIGGER_FALLBACKS = {
  manual: { label: "Manual", icon: "lucide:mouse-pointer-click", order: 10 },
  schedule: { label: "Schedule", icon: "lucide:clock", order: 20 },
  api: { label: "API endpoint", icon: "lucide:webhook", order: 30 },
  webhook: { label: "Webhook", icon: "lucide:radio", order: 40 },
  subworkflow: { label: "Sub-workflow", icon: "lucide:git-branch", order: 50 },
};

// Fields the server derives; never send them back.
export const DERIVED_TRIGGER_FIELDS = [
  "id",
  "workflow_graph_id",
  "workflow_public_id",
  "client_id",
  "next_run_at",
  "last_run_at",
  "last_run_status",
  "last_error",
  "run_count",
  "endpoint",
  "created_at",
  "updated_at",
];

/** @returns {{list: object[], byType: Record<string, object>}} ordered by the catalog's `order`. */
export const normalizeTriggerTypes = (payload) => {
  const types = payload?.types ?? {};
  const order = Array.isArray(payload?.trigger_types) ? payload.trigger_types : Object.keys(types);

  const list = order
    .filter((type) => typeof type === "string")
    .map((type) => ({ type, ...(TRIGGER_FALLBACKS[type] ?? {}), ...(types[type] ?? {}) }))
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  return {
    list,
    byType: Object.fromEntries(list.map((entry) => [entry.type, entry])),
  };
};

/** `show_if` is `{field: value}` or `{field: [values]}`. */
export const isFieldVisible = (field, values = {}) => {
  const showIf = field?.show_if;
  if (!showIf || typeof showIf !== "object") return true;

  return Object.entries(showIf).every(([key, expected]) => {
    const allowed = Array.isArray(expected) ? expected : [expected];
    return allowed.some((candidate) => candidate === values?.[key]);
  });
};

/** The value a field starts with: the draft's value, else the catalog default. */
export const initialFieldValue = (field, values = {}) => {
  const name = field?.name;
  if (name && values?.[name] !== undefined) return values[name];
  return field?.default;
};

/** Only the fields the catalog lists (and that are currently visible) go into `config`. */
export const buildConfigFromValues = (fields = [], values = {}) => {
  const config = {};

  fields.forEach((field) => {
    const name = field?.name;
    if (!name) return;
    if (!isFieldVisible(field, values)) return;

    const value = values[name];
    if (value === undefined || value === null || value === "") return;

    config[name] = value;
  });

  return config;
};

/** Parse JSON-typed fields; returns `{config, errors}` where errors map field → message. */
export const buildTypedConfig = (fields = [], values = {}) => {
  const config = buildConfigFromValues(fields, values);
  const errors = {};

  fields.forEach((field) => {
    const name = field?.name;
    if (!name || !(name in config)) return;

    const control = String(field?.control ?? "").toLowerCase();

    if (control === "json" || control === "json-object") {
      const raw = config[name];
      if (typeof raw !== "string") {
        if (control === "json-object" && (raw === null || typeof raw !== "object" || Array.isArray(raw))) {
          errors[name] = "Enter a JSON object.";
        }
        return;
      }

      try {
        const parsed = JSON.parse(raw);
        if (control === "json-object" && (parsed === null || typeof parsed !== "object" || Array.isArray(parsed))) {
          errors[name] = "Enter a JSON object.";
        } else {
          config[name] = parsed;
        }
      } catch (error) {
        errors[name] = "Invalid JSON.";
      }
    }

    if (control === "number" && typeof config[name] === "string") {
      const parsed = Number(config[name]);
      config[name] = Number.isFinite(parsed) ? parsed : config[name];
    }
  });

  return { config, errors };
};

/**
 * The trigger payload the definition save / CRUD endpoints accept: only the documented fields,
 * with no derived values and no `public_id` for a new trigger.
 */
export const buildTriggerPayload = (trigger = {}) => {
  const payload = {
    type: trigger.type,
    name: trigger.name ?? "",
    description: trigger.description ?? "",
    enabled: trigger.enabled !== false,
    config: trigger.config ?? {},
  };

  if (trigger.public_id) payload.public_id = trigger.public_id;
  if (trigger.api_key_name) payload.api_key_name = trigger.api_key_name;

  return payload;
};

/** Drop derived fields from a server trigger before it becomes an editable draft. */
export const toTriggerDraft = (trigger = {}) => ({
  public_id: trigger.public_id ?? null,
  type: trigger.type ?? "manual",
  name: trigger.name ?? "",
  description: trigger.description ?? "",
  enabled: trigger.enabled !== false,
  api_key_name: trigger.api_key_name ?? null,
  config: { ...(trigger.config ?? {}) },
});

/* ------------------------------------------------------------------ formatting */

/** A UTC timestamp rendered in the user's locale/timezone. */
export const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** "Every 1 hour", "Every 90 minutes", … */
export const formatInterval = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return null;

  if (value % 3600 === 0) {
    const hours = value / 3600;
    return `Every ${hours} hour${hours === 1 ? "" : "s"}`;
  }
  if (value % 60 === 0) {
    const minutes = value / 60;
    return `Every ${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  return `Every ${value} seconds`;
};

/** A one-line description of what a trigger does, for the card and the header summary. */
export const describeTrigger = (trigger = {}) => {
  const config = trigger.config ?? {};

  switch (trigger.type) {
    case "schedule": {
      if (config.cron) return `Cron ${config.cron}`;
      const interval = formatInterval(config.interval_seconds);
      if (interval) return interval;
      if (config.run_at) return `Once at ${formatDateTime(config.run_at)}`;
      return "Scheduled";
    }
    case "api":
      return trigger.endpoint?.path ? `API ${trigger.endpoint.path}` : "API endpoint";
    case "webhook":
      return trigger.endpoint?.path ? `Webhook ${trigger.endpoint.path}` : "Webhook";
    case "subworkflow": {
      const callers = Array.isArray(config.workflow_public_ids) ? config.workflow_public_ids : [];
      if (callers.length === 0) return "Any workflow";
      return `Called by ${callers.length} workflow${callers.length === 1 ? "" : "s"}`;
    }
    default:
      return "Manual";
  }
};

/** Header summary: "Manual only", or a joined description of the enabled triggers. */
export const summarizeTriggers = (triggers = []) => {
  const list = Array.isArray(triggers) ? triggers : [];
  if (list.length === 0) return "Manual only";

  const enabled = list.filter((trigger) => trigger?.enabled !== false);
  if (enabled.length === 0) return "All triggers paused";

  const descriptions = enabled.map(describeTrigger);
  return [...new Set(descriptions)].slice(0, 4).join(" · ");
};

/** Localised next run, with "Paused" / "—" for triggers that will not fire. */
export const describeNextRun = (trigger = {}) => {
  if (trigger.enabled === false) return "Paused";
  if (!trigger.next_run_at) return "—";
  return `Next run ${formatDateTime(trigger.next_run_at)}`;
};

export const RUN_STATUSES = ["queued", "running", "succeeded", "failed", "canceled", "skipped"];

export const runStatusTone = (status) => {
  switch (String(status ?? "").toLowerCase()) {
    case "succeeded":
      return "#2ecc71";
    case "failed":
      return "#e74c3c";
    case "running":
      return "#3498db";
    case "queued":
      return "#f39c12";
    case "canceled":
    case "skipped":
      return "#95a5a6";
    default:
      return "#95a5a6";
  }
};

/** "1.8s" / "340ms" from `duration_ms`. */
export const formatDuration = (durationMs) => {
  const value = Number(durationMs);
  if (!Number.isFinite(value) || value < 0) return "—";
  if (value < 1000) return `${Math.round(value)}ms`;
  const seconds = value / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${Math.round(seconds % 60)}s`;
};

export { isoToLocalInput, localInputToIso } from "./datetime";

/** A usable IANA timezone list, with a safe fallback when the browser lacks the API. */
export const timezoneOptions = () => {
  try {
    if (typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch (error) {
    // fall through
  }
  return ["UTC"];
};

/** The browser's timezone, used as the schedule default. */
export const guessTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (error) {
    return "UTC";
  }
};
